<template>
  <div class="activity-tile">
    <span class="icon" :class="isBuy ? 'buy' : 'sell'">
      <ExIcon :name="isBuy ? 'arrow-down' : 'tag'" />
    </span>
    <p class="sentence">
      <template v-if="isBuy">
        An investor purchased <strong>{{ amountLabel }}</strong> of
      </template>
      <template v-else>
        An investor listed <strong>{{ qtyLabel }} </strong> units of 
      </template>
      <router-link v-if="route" :to="route" class="token-link">{{ tokenName }}</router-link>
      <span v-else class="token-plain">{{ tokenName }}</span>
      <template v-if="!isBuy"> tokens for sale</template>
    </p>
    <span class="time">{{ time }}</span>
  </div>
</template>

<script>
import ExIcon from "./ExIcon.vue";
import * as ex from "@/utils/exchange.js";

export default {
  name: "ActivityTile",
  components: { ExIcon },
  props: {
    activity: { type: Object, required: true },
    // Route to the token detail page, or null when the token can't be resolved.
    route: { type: Object, default: null },
  },
  computed: {
    isBuy() {
      const t = (
        this.activity.type ||
        this.activity.side ||
        this.activity.action ||
        ""
      ).toLowerCase();
      return t === "buy" || t === "purchase" || t === "bought";
    },
    token() {
      return this.activity.token || this.activity.asset || this.activity.tokenInfo || {};
    },
    tokenName() {
      return this.token.name || this.activity.name || "an investment";
    },
    volume() {
      return (
        this.activity.volume ??
        this.activity.orderAmount ??
        this.activity.quantity ??
        this.activity.units ??
        0
      );
    },
    // Money value of a buy. The feed sends `amount` (sometimes null); fall back
    // to volume × price, then to a raw unit count.
    amountLabel() {
      const money =
        this.activity.transactionAmount ??
        this.activity.amount ??
        this.activity.value ??
        this.activity.totalAmount;
      const price = this.activity.price;
      if (money != null && money !== 0) {
        return ex.money(money, ex.tokenCurrency(this.token), 0);
      }
      if (price != null && this.volume) {
        return ex.money(this.volume * price, ex.tokenCurrency(this.token), 0);
      }
      return `${ex.compact(this.volume)} units`;
    },
    qtyLabel() {
      return ex.compact(this.volume);
    },
    time() {
      return ex.relativeTime(
        this.activity.createdAt || this.activity.created_at || this.activity.date
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;

  .ex-icon {
    font-size: 16px;
  }

  &.buy {
    background: rgba(77, 132, 255, 0.12);
    color: #4d84ff;
  }

  &.sell {
    background: rgba(0, 18, 64, 0.08);
    color: #001240;
  }
}

.sentence {
  flex: 1;
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #4f5877;
  line-height: 1.4;

  strong {
    font-weight: 500;
    color: #001240;
  }
}

.token-link {
  color: #4e84ff;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.token-plain {
  font-weight: 500;
  color: #001240;
}

.time {
  font-size: 10px;
  color: #8791a7;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
